/**
 * s222 LANE-2 — the words a screen takes from the board's TEMPLATE, not from `steps()`.
 *
 * Personal details (`you`) is drawn by the board's HTML template (`Main.dc.html`, `view.isFields`) rather than by
 * the `steps()` data that `board-steps.generated.ts` extracts. Its words cannot be generated the same way, so they
 * are held here — COPIED from the board, never written — and `server/__tests__/board-template-copy.test.ts` fails
 * when any of them is no longer found, verbatim, inside that screen's block of the COMMITTED board. A board edit
 * that changes one of these words therefore fails the suite until this file follows it.
 *
 * s233 (D-233.68, D-233.75): the old `you` and `facts` screens are ONE screen, in the board's order — the name, the
 * home address, the birth details, the phone, the email. The hints are gone (rule 146) and so is the Province box
 * (the home address carries the province; `clients.jurisdiction_province` keeps its other writers).
 *
 * Board line references are to v443.
 */

/** Personal details — `view.isFields`, Main.dc.html lines 94–188, plus the view code's empty texts (`cobText`, the
 *  country box; `altEmails`' `ph`, an other-email box). Keyed by the About You field each answers. */
export const PERSONAL_TEMPLATE = {
  first_name: { label: "First name(s)" },
  middle_name: { label: "Middle name" },
  family_name: { label: "Surname" },
  home_address: { label: "Home address", placeholder: "Start typing your address" },
  // s237 (D-236.47(1), D-236.88-6): the home's tenure, asked once under the address it describes.
  home_tenure: { label: "Is this home…" },
  date_of_birth: { label: "Date of birth" },
  place_of_birth: { label: "City or town of birth", placeholder: "e.g. Brantford" },
  country_of_birth: { label: "Country of birth", placeholder: "e.g. Canada" },
  province_of_birth: { label: "Province or territory of birth", placeholder: "e.g. Ontario" },
  name_at_birth: { label: "Name at birth", placeholder: "Only if different from today" },
  mother_maiden_name: { label: "Mother’s maiden name", placeholder: "Last name before marriage" },
  last_occupation: { label: "Occupation", placeholder: "e.g. Engineer, or Teacher (retired)" },
  mobile: { label: "Mobile number" },
  phone_make_model: { label: "Phone make and model", placeholder: "e.g. iPhone 15 Pro" },
  email: { label: "Email" },
  other_emails: { label: "Any other email addresses you use", placeholder: "me@example.com", add: "Add another" },
} as const;

/** s237 (D-236.47(1), D-236.88-6): the home's three answers and the ONE note under them — the board's view code
 *  (`tenureOpts`, `tenureNoteText`, Main.dc.html). Own and rent share a note; family has its own. */
export const TENURE_TEMPLATE = {
  options: { own: "Owned", rent: "Rented", family: "With family, or in a care home" },
  notes: { own: "Chapter two lists it first, as your primary residence.", rent: "Chapter two lists it first, as your primary residence.", family: "Chapter two asks whose home it is, or the name of the care home." },
} as const;

/** s237 PR 4 (D-236.47(2), D-236.88-6, -7): chapter two's home, carried from Personal details — the Chapter2 board's
 *  `blk.isLive` block (heading, the one line, the link) and the line's answer word (`liveNow`). The address is the
 *  planner's own (the board's sample is 40 Raymond Ave, Toronto). */
export const CH2_HOME_TEMPLATE = {
  head: "Your home",
  from: "from Personal details.",
  change: "Change it there",
  now: { own: "owned", rent: "rented", family: "with family, or in a care home" },
} as const;

/** Where the screen's block sits in the board's source: its opening `sc-if` and the next screen's. */
export const TEMPLATE_BLOCKS = {
  you: { open: '<sc-if value="{{ view.isFields }}"', close: '<sc-if value="{{ view.isGrid }}"' },
} as const;
